import React, { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Repeat } from 'lucide-react';
import { Modal } from '@components/Dialog';
import { Button } from '@components/Button';
import { DateInputBR } from '@components/DateInputBR';
import { WEEKDAYS_PT } from '@components/dateInputUtils';
import boardService from '@services/boardService';
import recurrenceService from '@services/recurrenceService';
import type { Priority } from '@/types/activity';
import type { Board } from '@/types/board';
import type { Frequency, RecurrenceFormData, RecurrenceRule } from '@/types/planning';
import { PRIORITIES, PRIORITY_LABELS } from '@/utils/priority';
import { todayISO } from '@/utils/formatters';
import {
  FREQUENCY_LABELS,
  PRESET_OPTIONS,
  RecurrencePreset,
  describeFrequency,
  frequencyToPreset,
  hasWeekday,
  presetToFrequency,
  toggleWeekday,
} from '@/utils/recurrence';

/** Valores iniciais vindos do "Tornar recorrente" do modal de atividade. */
export interface RecurrencePrefill {
  title?: string;
  description?: string;
  board_id?: number;
  board_column_id?: number;
  priority?: Priority;
  start_date?: string;
}

interface Props {
  rule?: RecurrenceRule | null;
  prefill?: RecurrencePrefill;
  onClose: () => void;
  onSaved: () => void;
  onError: (err: unknown) => void;
}

interface FormState {
  title: string;
  description: string;
  board_id: number | '';
  board_column_id: number | '';
  priority: Priority;
  preset: RecurrencePreset;
  frequency: Frequency;
  interval_count: number;
  days_of_week: number;
  day_of_month: number;
  start_date: string;
  end_date: string;
  lead_days: number;
  is_active: boolean;
}

const labelCls = 'text-label block mb-1';

export const RecurrenceFormModal: React.FC<Props> = ({ rule, prefill, onClose, onSaved, onError }) => {
  const isEdit = !!rule;
  const [boards, setBoards] = useState<Board[]>([]);
  const [board, setBoard] = useState<Board | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(() => {
    if (rule) {
      return {
        title: rule.title,
        description: rule.description ?? '',
        board_id: rule.board_id,
        board_column_id: rule.board_column_id ?? '',
        priority: rule.priority,
        preset: frequencyToPreset(rule.frequency, rule.interval_count),
        frequency: rule.frequency,
        interval_count: rule.interval_count,
        days_of_week: rule.days_of_week,
        day_of_month: rule.day_of_month,
        start_date: rule.start_date,
        end_date: rule.end_date ?? '',
        lead_days: rule.lead_days,
        is_active: rule.is_active,
      };
    }
    return {
      title: prefill?.title ?? '',
      description: prefill?.description ?? '',
      board_id: prefill?.board_id ?? '',
      board_column_id: prefill?.board_column_id ?? '',
      priority: prefill?.priority ?? 'medium',
      preset: 'weekly',
      frequency: 'weekly',
      interval_count: 1,
      days_of_week: 0,
      day_of_month: 0,
      start_date: prefill?.start_date ?? todayISO(),
      end_date: '',
      lead_days: 0,
      is_active: true,
    };
  });

  useEffect(() => {
    boardService
      .getAll()
      .then((list) => {
        setBoards(list);
        setForm((f) => {
          if (f.board_id) return f;
          const main = list.find((b) => b.is_main) ?? list[0];
          return main ? { ...f, board_id: main.id } : f;
        });
      })
      .catch(onError);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!form.board_id) {
      setBoard(null);
      return;
    }
    let cancelled = false;
    boardService
      .getById(Number(form.board_id))
      .then((b) => !cancelled && setBoard(b))
      .catch((err) => !cancelled && onError(err));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.board_id]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) => setForm((f) => ({ ...f, [key]: value }));

  const changePreset = (preset: RecurrencePreset) => {
    const pair = presetToFrequency(preset);
    setForm((f) => ({
      ...f,
      preset,
      ...(pair ? { frequency: pair.frequency, interval_count: pair.interval_count } : {}),
    }));
  };

  const changeCustom = (frequency: Frequency, interval: number) => {
    setForm((f) => ({ ...f, frequency, interval_count: interval }));
  };

  const dateError = form.end_date && form.end_date < form.start_date ? 'O fim não pode ser antes do início.' : null;
  const canSubmit =
    !!form.board_id &&
    form.title.trim().length > 0 &&
    !!form.start_date &&
    form.interval_count >= 1 &&
    form.interval_count <= 60 &&
    !dateError &&
    !saving;

  const handleSave = async () => {
    if (!canSubmit) return;
    const payload: RecurrenceFormData = {
      board_id: Number(form.board_id),
      board_column_id: form.board_column_id ? Number(form.board_column_id) : null,
      title: form.title.trim(),
      description: form.description.trim() || null,
      priority: form.priority,
      frequency: form.frequency,
      interval_count: form.interval_count,
      days_of_week: form.frequency === 'weekly' ? form.days_of_week : 0,
      day_of_month: form.frequency === 'monthly' ? form.day_of_month : 0,
      start_date: form.start_date,
      end_date: form.end_date || null,
      lead_days: form.lead_days,
      ...(isEdit ? { is_active: form.is_active } : {}),
    };
    setSaving(true);
    try {
      if (rule) await recurrenceService.update(rule.id, payload);
      else await recurrenceService.create(payload);
      onSaved();
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} size="xl" closeOnBackdrop={!saving}>
      <div className="px-6 py-4 border-b border-slate-200 flex items-center gap-2">
        <Repeat size={18} className="text-slate-500" />
        <h2 className="text-h3">{isEdit ? 'Editar recorrência' : 'Nova recorrência'}</h2>
      </div>

      <div className="px-6 py-4 max-h-[65vh] overflow-y-auto custom-scrollbar grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className={labelCls}>
            Título <span className="text-red-500">*</span>
          </label>
          <input
            className="input-field"
            maxLength={300}
            value={form.title}
            onChange={(e) => set('title', e.target.value)}
            placeholder="Ex.: Pagar a fatura do cartão"
          />
        </div>
        <div className="md:col-span-2">
          <label className={labelCls}>Descrição</label>
          <textarea
            className="textarea-field"
            rows={3}
            maxLength={8000}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>

        <div>
          <label className={labelCls}>
            Quadro <span className="text-red-500">*</span>
          </label>
          <select
            className="select-field"
            value={form.board_id}
            onChange={(e) =>
              setForm((f) => ({ ...f, board_id: e.target.value ? Number(e.target.value) : '', board_column_id: '' }))
            }
          >
            <option value="">Selecione…</option>
            {boards.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Coluna</label>
          <select
            className="select-field"
            value={form.board_column_id}
            onChange={(e) => set('board_column_id', e.target.value ? Number(e.target.value) : '')}
          >
            <option value="">Primeira coluna aberta</option>
            {(board?.columns ?? [])
              .filter((c) => !c.is_done_column)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>Prioridade</label>
          <select className="select-field" value={form.priority} onChange={(e) => set('priority', e.target.value as Priority)}>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
        </div>

        <div className="md:col-span-2 border-t border-slate-100 pt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className={labelCls}>Repetição</label>
            <select
              className="select-field"
              value={form.preset}
              onChange={(e) => changePreset(e.target.value as RecurrencePreset)}
            >
              {PRESET_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {form.preset === 'custom' && (
            <>
              <div>
                <label className={labelCls}>A cada</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  className="input-field"
                  value={form.interval_count}
                  onChange={(e) => changeCustom(form.frequency, Math.max(1, Number(e.target.value) || 1))}
                />
              </div>
              <div>
                <label className={labelCls}>Unidade</label>
                <select
                  className="select-field"
                  value={form.frequency}
                  onChange={(e) => changeCustom(e.target.value as Frequency, form.interval_count)}
                >
                  {(Object.keys(FREQUENCY_LABELS) as Frequency[]).map((f) => (
                    <option key={f} value={f}>
                      {FREQUENCY_LABELS[f]}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {form.preset !== 'custom' && (
            <div className="md:col-span-2 flex items-end pb-2 text-sm text-slate-500">
              {describeFrequency(form.frequency, form.interval_count)}
            </div>
          )}
        </div>

        {form.frequency === 'weekly' && (
          <div className="md:col-span-2">
            <label className={labelCls}>Dias da semana</label>
            <div className="flex flex-wrap gap-2">
              {WEEKDAYS_PT.map((wd, i) => {
                const on = hasWeekday(form.days_of_week, i);
                return (
                  <button
                    key={wd}
                    type="button"
                    onClick={() => set('days_of_week', toggleWeekday(form.days_of_week, i))}
                    className={clsx(
                      'w-12 py-1.5 rounded-lg border text-sm font-medium transition-colors',
                      on ? 'bg-emerald-600 border-emerald-600 text-white' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50',
                    )}
                    aria-pressed={on}
                  >
                    {wd}
                  </button>
                );
              })}
            </div>
            <p className="text-small mt-1">Nenhum marcado = mesmo dia da semana da data de início.</p>
          </div>
        )}

        {form.frequency === 'monthly' && (
          <div>
            <label className={labelCls}>Dia do mês</label>
            <input
              type="number"
              min={0}
              max={31}
              className="input-field"
              value={form.day_of_month}
              onChange={(e) => set('day_of_month', Math.min(31, Math.max(0, Number(e.target.value) || 0)))}
            />
            <p className="text-small mt-1">0 = mesmo dia da data de início.</p>
          </div>
        )}

        <div>
          <label className={labelCls}>
            Início <span className="text-red-500">*</span>
          </label>
          <DateInputBR className="input-field w-full" value={form.start_date} onChange={(v) => set('start_date', v)} />
        </div>
        <div>
          <label className={labelCls}>Fim (opcional)</label>
          <DateInputBR
            className="input-field w-full"
            value={form.end_date}
            min={form.start_date || undefined}
            onChange={(v) => set('end_date', v)}
            placeholder="sem fim"
          />
        </div>
        <div>
          <label className={labelCls}>Antecedência (dias)</label>
          <input
            type="number"
            min={0}
            max={60}
            className="input-field"
            value={form.lead_days}
            onChange={(e) => set('lead_days', Math.min(60, Math.max(0, Number(e.target.value) || 0)))}
          />
          <p className="text-small mt-1">A atividade é criada esses dias antes do prazo (prazo = data da ocorrência).</p>
        </div>
        {isEdit && (
          <div className="flex items-center">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                className="checkbox-field"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
              />
              Ativa
            </label>
          </div>
        )}
        {dateError && <p className="md:col-span-2 text-sm text-red-600">{dateError}</p>}
      </div>

      <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-lg flex justify-end gap-2">
        <Button variant="outline" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button variant="primary" onClick={handleSave} loading={saving} disabled={!canSubmit}>
          {isEdit ? 'Salvar' : 'Criar recorrência'}
        </Button>
      </div>
    </Modal>
  );
};
